/**
 * Backend maintenance mode (`php artisan down` / `php artisan up`).
 *
 * DETECTION CONTRACT — `PreventRequestsDuringMaintenance` is registered as
 * GLOBAL middleware in lula-foresight-be (bootstrap/app.php), so while the app
 * is down it gates EVERY route and throws
 * `HttpException(503, 'Service Unavailable')` — that message is a hard-coded
 * literal in the framework middleware, not a translated or caller-supplied
 * string. Laravel's handler renders it for JSON requests as
 * `{ "message": "Service Unavailable" }`, in both debug and non-debug modes
 * (Foundation/Exceptions/Handler::convertExceptionToArray keeps `getMessage()`
 * for any HttpException).
 *
 * So maintenance mode is recognised by BOTH halves of that response, because
 * a 503 on its own is far too broad:
 *
 * 1. `message` is exactly `Service Unavailable`. Rules out a 503 raised by
 *    infrastructure rather than the app — an ALB with no healthy targets, a
 *    CloudFront or nginx error page — which answers with HTML or a different
 *    body and must NOT black out the whole app.
 * 2. There is no `code`. Every 503 the app raises DELIBERATELY goes through
 *    `Response::serviceUnavailable()` (app/Providers/ResponseServiceProvider.php)
 *    or an inline `response()->json([...])`, and all of them set a `code` so the
 *    client can tell which dependency degraded — the AI maintenance summary,
 *    LuMi resident access links, Yardi mapping redrive, event-ledger retries.
 *    Those are ordinary per-request failures the calling screen reports itself.
 *
 * Both conditions hold for a real `artisan down` and fail for everything else,
 * so a misconfigured dependency degrades one screen instead of the app.
 */

/**
 * The literal `PreventRequestsDuringMaintenance` puts in its `HttpException`.
 * Not translated and not caller-supplied, so it is safe to match exactly.
 */
const LARAVEL_MAINTENANCE_MESSAGE = 'We are currently under maintenance, Please try again later.';

/** The halves of a backend error body this check reads. */
type ErrorBody = { code?: unknown; message?: unknown };

export function isMaintenanceResponse(status: number | undefined, body: unknown): boolean {
  if (status !== 503) return false;
  // Anything that is not a JSON object — an HTML error page from a load
  // balancer or CDN, or an empty body — is infrastructure, not `artisan down`.
  if (!body || typeof body !== 'object') return false;

  const { code, message } = body as ErrorBody;
  return code === undefined && message === LARAVEL_MAINTENANCE_MESSAGE;
}

const MAINTENANCE_PROBE_PATH = 'api/health?fresh=true';

/**
 * The backend's own health route (`fresh=true` bypasses any caching so the
 * probe always reflects live state). It sits behind the same global
 * maintenance middleware as every other route, so it answers with the
 * maintenance 503 while `artisan down` and 200 once `artisan up` runs — and
 * it needs no auth, which matters because the poll has to work on the login
 * page too.
 */
export function buildMaintenanceProbeUrl(baseUrl: string | undefined): string {
  if (!baseUrl) return '';

  const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  return `${normalized}${MAINTENANCE_PROBE_PATH}`;
}

/**
 * Resolves `true` while the backend is still down.
 *
 * Probes the backend's health route directly rather than going through any
 * frontend proxy, reusing `isMaintenanceResponse()` — the same detection
 * contract used to flip into maintenance mode in the first place — so a 503
 * here is only read as "still down" when it's the global `artisan down` 503,
 * not an ordinary operational failure the health check itself might report
 * (those carry a `code` and shouldn't keep the user parked on this page).
 * Anything other than 503 short-circuits to "recovered" without needing to
 * parse the body, so a non-JSON 200 (or any other success shape) still counts.
 *
 * A rejected probe (CORS, DNS, dropped connection mid-deploy) is
 * indistinguishable from "still down" from the browser's side, so it resolves
 * `true`. Staying put is the safe read: a false "back up" would bounce the user
 * to a home page that immediately fails every request again.
 */
export async function probeIsStillDown(): Promise<boolean> {
  const url = buildMaintenanceProbeUrl(process.env.NEXT_PUBLIC_API_BASE_URL);
  if (!url) return true;

  try {
    const response = await fetch(url, { cache: 'no-store', credentials: 'omit' });
    if (response.status !== 503) return false;

    const body = await response.json();
    return isMaintenanceResponse(response.status, body);
  } catch {
    return true;
  }
}

/**
 * Hard navigation back to the app root once maintenance clears.
 *
 * A HARD navigation, deliberately: the Redux store, every cached list and any
 * half-failed page state accumulated behind the maintenance window are thrown
 * away and rebuilt against the healthy backend. `src/proxy.ts` then routes the
 * user from `/` to their real home (coordinator list view or vendor requests).
 *
 * Extracted so callers have a seam to assert on: jsdom exposes
 * `location.assign` as a non-writable, non-configurable own property, so it
 * cannot be spied or replaced in place. Tests mock THIS function instead — see
 * tests/unit/MaintenanceMode/MaintenanceGate.test.tsx.
 */
/* istanbul ignore next -- single delegation to an unmockable browser API */
export function redirectToHome(): void {
  window.location.assign('/');
}
